/**
 * Public front-page character meet-cards (Epic 20 S20.2, Task 5).
 *
 * Data-driven marquee from ORIGINALS_CHARACTERS — launch-ready (origin != null)
 * only, capped at 6, in array order. Each card links to the character detail
 * page (/originals/characters/{slug}, built in S20.16); a section link goes to
 * the index. Graceful art-null fallback (accent halo + initial, no broken img).
 * NO story-count line (owner 2026-05-31). Hardcoded Finnish (Epic 20 exception).
 *
 * The roster is fully data-driven: when the real characters replace today's
 * placeholders via an S20.3 content edit (~2026-06-07), no code change is needed.
 *
 * @module components/originals/home/CharacterMeetCards
 */

import { Link } from 'react-router-dom';
import { ORIGINALS_CHARACTERS, type OriginalsCharacter } from '@sagamoon/shared';

const MAX_CARDS = 6;

function MeetCard({ character }: { character: OriginalsCharacter }): JSX.Element {
  return (
    <Link
      to={`/originals/characters/${character.slug}`}
      className="group flex flex-col overflow-hidden rounded-2xl bg-surface-cream shadow-md transition-dreamy hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2 focus:ring-offset-twilight-deep"
    >
      <div
        className="flex h-44 items-center justify-center"
        style={{ backgroundColor: character.accent }}
      >
        {character.art ? (
          <img
            src={character.art}
            alt={`${character.name} — Originals-hahmo`}
            className="h-full w-full object-contain"
            loading="lazy"
          />
        ) : (
          <div className="text-center text-text-twilight-warm-brown">
            <span className="font-whimsy text-4xl">{character.name[0]}</span>
            <p className="text-xs">Hahmokuva tulossa</p>
          </div>
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1 p-4">
        <h3 className="font-whimsy text-lg text-text-twilight-warm-brown">{character.name}</h3>
        <p className="text-sm font-medium text-text-twilight-warm-brown/70">{character.species}</p>
        <p className="mt-1 line-clamp-3 text-sm text-text-twilight-warm-brown/80">
          {character.blurb}
        </p>
        <span className="mt-auto inline-flex min-h-11 items-center pt-2 text-sm font-semibold text-accent-gold">
          Avaa hahmo
          <span
            aria-hidden="true"
            className="ml-1 transition-transform group-hover:translate-x-0.5"
          >
            →
          </span>
        </span>
      </div>
    </Link>
  );
}

export function CharacterMeetCards(): JSX.Element {
  const launchReady = ORIGINALS_CHARACTERS.filter((c) => c.origin != null).slice(0, MAX_CARDS);

  return (
    <section className="px-4 py-12 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-accent-gold">
          Tutustu esimerkkihahmoihin
        </p>
        <h2 className="mt-2 font-whimsy text-3xl text-text-twilight-cream">
          Jokaisella sankarilla on oma persoona.
        </h2>
        <p className="mt-4 max-w-2xl text-text-twilight-cream/80">
          Valokuvasta tai piirustuksesta syntyy hahmo, jolla on oma nimi, oma luonne ja oma tapa
          seikkailla. Kun hahmo tuntuu tutulta, lapsen on helppo palata sen luo uudessa sadussa.
        </p>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {launchReady.map((character) => (
            <MeetCard key={character.slug} character={character} />
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/originals/characters"
            className="inline-flex min-h-11 items-center text-sm font-semibold text-accent-gold hover:opacity-90"
          >
            Tutustu esimerkkihahmoihin
            <span aria-hidden="true" className="ml-1">
              →
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
